/**
 * Step 2: first name, last name, birthday, phone, and the disabled sign-in
 * email. The required set is exactly first name and birthday (the
 * onboarding feature doc's constraint 3); last name and phone are optional
 * per AUTH-14. `Continue` writes the answers plus the next `onboardingStep`
 * to `/profile/update`, so a browser reload resumes from the server and
 * never from a client counter.
 */
import { useIdentityGet } from "@sportsmet/auth-sdk/private";
import { useProfileUpdate } from "@sportsmet/profile-sdk/private";
import { Button, Input } from "@sportsmet/ui";
import { type ReactNode, useRef, useState } from "react";

import { showApiError } from "../../errors/apiErrorBus";
import {
  BIRTHDAY_LABEL,
  BIRTHDAY_PATTERN,
  FIRST_NAME_LABEL,
  isAtLeastMinimumAge,
} from "../editProfile/editProfileLogic";
import {
  ABOUT_YOU_HEADLINE,
  ABOUT_YOU_SUB,
  BIRTHDAY_AGE_HINT,
  CONTINUE_LABEL,
  CONTINUE_PENDING_LABEL,
  EMAIL_HINT,
  EMAIL_LABEL,
  ONBOARDING_LAST_NAME_LABEL,
  ONBOARDING_PHONE_LABEL,
} from "./onboardingCopy";
import { OnboardingStepFrame } from "./OnboardingStepFrame";
import { UnderAgeRefusal } from "./UnderAgeRefusal";

export type typeAboutYouForm = {
  firstName: string;
  lastName: string;
  birthday: string; // "YYYY-MM-DD", straight off the native date input
  phone: string;
};

export const AboutYouStep = ({
  stepIndex,
  stepTotal,
  initialForm,
  nextServerStep,
  onSaved,
}: {
  /** This fan's position and total, per Q274. Passed straight to OnboardingStepFrame. */
  stepIndex: number;
  stepTotal: number;
  /** Prefilled from the stored profile. This is the resume state. */
  initialForm: typeAboutYouForm;
  /** The `onboardingStep` value to write on Continue. */
  nextServerStep: "signInMethods" | "photo" | "sports";
  /**
   * Called after `/profile/update` succeeds. The route advances the step
   * and raises its ceiling from the `reachedServerStep` value passed here,
   * which came straight off the update response.
   */
  onSaved: (input: { reachedServerStep: string | null }) => void;
}): ReactNode => {
  const identity = useIdentityGet({});
  const update = useProfileUpdate();
  const [form, setForm] = useState<typeAboutYouForm>(initialForm);
  const [isRefusing, setIsRefusing] = useState(false);
  const isSaving = useRef(false);

  const email = identity.data?.data?.email ?? "";
  const isBirthdayComplete = BIRTHDAY_PATTERN.test(form.birthday);
  const isUnderAge =
    isBirthdayComplete &&
    !isAtLeastMinimumAge({ birthday: form.birthday, now: new Date() });

  function handleBirthdayChange(value: string): void {
    setForm((current) => ({ ...current, birthday: value }));
    const isComplete = BIRTHDAY_PATTERN.test(value);
    // The refusal follows the field in both directions: correcting a
    // mistyped year disarms the destructive button instead of leaving it
    // live next to a birthday the fan has already fixed.
    setIsRefusing(
      isComplete && !isAtLeastMinimumAge({ birthday: value, now: new Date() }),
    );
  }

  async function handleContinue(): Promise<void> {
    if (isSaving.current) return;
    isSaving.current = true;

    let result;
    try {
      result = await update.mutateAsync({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim() === "" ? null : form.lastName.trim(),
        birthday: form.birthday,
        phone: form.phone.trim() === "" ? null : form.phone.trim(),
        onboardingStep: nextServerStep,
      });
    } catch {
      showApiError({ error: { code: "INTERNAL_CALL_FAILED", message: "" } });
      isSaving.current = false;
      return;
    }

    if (showApiError(result)) {
      isSaving.current = false;
      return;
    }

    onSaved({
      reachedServerStep: result.data?.profile.onboardingStep ?? nextServerStep,
    });
  }

  if (isRefusing) {
    return (
      <OnboardingStepFrame
        stepIndex={stepIndex}
        stepTotal={stepTotal}
        headline={ABOUT_YOU_HEADLINE}
      >
        <Input
          label={BIRTHDAY_LABEL}
          required
          type="date"
          hint={BIRTHDAY_AGE_HINT}
          error={BIRTHDAY_AGE_HINT}
          data-testid="onboarding-birthday"
          value={form.birthday}
          onChange={(event) => {
            handleBirthdayChange(event.target.value);
          }}
        />
        <UnderAgeRefusal
          onCancel={() => {
            setIsRefusing(false);
          }}
        />
      </OnboardingStepFrame>
    );
  }

  return (
    <OnboardingStepFrame
      stepIndex={stepIndex}
      stepTotal={stepTotal}
      headline={ABOUT_YOU_HEADLINE}
      sub={ABOUT_YOU_SUB}
    >
      <div className="flex flex-col gap-4 lg:flex-row lg:gap-4">
        <Input
          label={FIRST_NAME_LABEL}
          required
          autoComplete="given-name"
          data-testid="onboarding-first-name"
          value={form.firstName}
          onChange={(event) => {
            setForm((current) => ({
              ...current,
              firstName: event.target.value,
            }));
          }}
        />
        <Input
          label={ONBOARDING_LAST_NAME_LABEL}
          autoComplete="family-name"
          data-testid="onboarding-last-name"
          value={form.lastName}
          onChange={(event) => {
            setForm((current) => ({
              ...current,
              lastName: event.target.value,
            }));
          }}
        />
      </div>
      <div className="flex flex-col gap-4 lg:flex-row lg:gap-4">
        <Input
          label={BIRTHDAY_LABEL}
          required
          type="date"
          hint={BIRTHDAY_AGE_HINT}
          data-testid="onboarding-birthday"
          value={form.birthday}
          onChange={(event) => {
            handleBirthdayChange(event.target.value);
          }}
        />
        <Input
          label={ONBOARDING_PHONE_LABEL}
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          data-testid="onboarding-phone"
          value={form.phone}
          onChange={(event) => {
            setForm((current) => ({ ...current, phone: event.target.value }));
          }}
        />
      </div>
      <Input
        label={EMAIL_LABEL}
        type="email"
        disabled
        readOnly
        value={email}
        hint={EMAIL_HINT}
        data-testid="onboarding-email"
      />
      <Button
        variant="primary"
        fullWidth
        loading={update.isPending}
        loadingLabel={CONTINUE_PENDING_LABEL}
        disabled={
          form.firstName.trim() === "" || !isBirthdayComplete || isUnderAge
        }
        data-testid="onboarding-about-you-continue"
        onClick={() => {
          void handleContinue();
        }}
      >
        {CONTINUE_LABEL}
      </Button>
    </OnboardingStepFrame>
  );
};
